import { handleError } from './apiUtils'
import { getConfigurationAsync, xKMSClient } from './httpMethods'
import {
  SearchUsagePermissionsRequestDto,
  SearchUsagePermissionsResponseDto,
  UsagePermissionRequestDto,
  UsagePermissionResponseDto,
  UsagePermissionsApi,
} from './kmsapiclient'
/**
 * Searches for usage permissions based on the provided request.
 *
 * @param {SearchUsagePermissionsRequestDto} request - The request object containing search criteria.
 * @returns {Promise<SearchUsagePermissionsResponseDto>} - A promise that resolves to the search response.
 */
export async function searchUsagePermissionsAsync(
  request: SearchUsagePermissionsRequestDto,
): Promise<SearchUsagePermissionsResponseDto> {
  try {
    const usagePermissionsApi = new UsagePermissionsApi(getConfigurationAsync())
    const response = (
      await usagePermissionsApi.usagePermissionsSearch(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Retrieves a usage permission based on the provided request.
 *
 * @param {string} id - The ID of the usage permission to retrieve.
 * @returns {Promise<UsagePermissionResponseDto>} - A promise that resolves to the usage permission response.
 */
export async function getUsagePermissionAsync(request) {
  try {
    const usagePermissionsApi = new UsagePermissionsApi(getConfigurationAsync())
    const response = (
      await usagePermissionsApi.usagePermissionGet(request.id, xKMSClient)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Adds a usage permission based on the provided request.
 *
 * @param {UsagePermissionRequestDto} request - The request object containing the usage permission to add.
 * @returns {Promise<UsagePermissionResponseDto>} - A promise that resolves to the usage permission response.
 */
export async function addUsagePermissionAsync(
  request: UsagePermissionRequestDto,
): Promise<UsagePermissionResponseDto> {
  try {
    const usagePermissionsApi = new UsagePermissionsApi(getConfigurationAsync())
    const response = (
      await usagePermissionsApi.usagePermissionAdd(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Removes a usage permission based on the provided request.
 *
 * @param {string} id - The ID of the usage permission to remove.
 * @returns {Promise<string>} - A promise that resolves to the status of the removal.
 */
export async function removeUsagePermissionAsync(id: string): Promise<string> {
  try {
    const usagePermissionsApi = new UsagePermissionsApi(getConfigurationAsync())
    const response = (await usagePermissionsApi.usagePermissionDelete(id)).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Updates a usage permission based on the provided request.
 *
 * @param {string} id - The ID of the usage permission to update.
 * @param {UsagePermissionRequestDto} request - The request object containing the usage permission to update.
 * @returns {Promise<UsagePermissionResponseDto>} - A promise that resolves to the usage permission response.
 */
export async function updateUsagePermissionAsync(
  id: string,
  request: UsagePermissionRequestDto,
): Promise<UsagePermissionResponseDto> {
  try {
    const usagePermissionsApi = new UsagePermissionsApi(getConfigurationAsync())
    const response = (
      await usagePermissionsApi.usagePermissionUpdate(id, xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}
